// Sentinel-X enrollment-stage control.
//
// The brain's auto-enrollment derives a default stage from the
// `sensitivity` slider (LOW→shadow, MEDIUM→canary, HIGH→
// authoritative). This component lets operators either track that
// mapping (Auto on → no override row; brain uses the derived stage)
// or pin a specific stage per site. Pinning most commonly happens
// to promote faster than the slider implies (jump straight to
// `authoritative`).
//
// Writes go to PUT /api/user/v1/config/ddos/:site_uid/stage. The
// brain picks up changes on its 60s refresh ticker. A `null`
// stage means no explicit row — brain uses the sensitivity-
// derived default (surfaced as Auto in this UI).

import React from "react";
import { Box, Stack, Typography } from "@mui/material";

import colors from "../../../../../utils/colors";
import { GreenSwitch } from "../../../../common/Greens";
import useDdosConfig from "../../../../../hooks/user/useDdosConfig";

// Palette tokens — kept in sync with DDosMigrationConfig / drawer.
const INDIGO = colors.primary[300];
const INDIGO_BG = "rgba(99, 113, 247, 0.08)";
const INDIGO_RING = "rgba(99, 113, 247, 0.22)";
const BORDER = colors.neutral[200];
const BORDER_STRONG = colors.neutral[500];
const FG1 = colors.neutral[1000];
const FG2 = colors.neutral[700];
const FG3 = colors.neutral[500];

// Three primary stages shown as side-by-side cards. The `from` tag
// in the corner names the sensitivity that maps to this stage when
// Auto is on — so operators scanning the row understand why Auto
// landed where it did.
const STAGE_CARDS = [
  { value: "shadow",        label: "Shadow",        from: "Low",    desc: "Verdicts are logged, never enforced." },
  { value: "canary",        label: "Canary",        from: "Medium", desc: "Enforces on a sample of traffic." },
  { value: "authoritative", label: "Authoritative", from: "High",   desc: "Every verdict enforces." },
];

const AUTO_STAGE_FROM_SENSITIVITY = { 1: "shadow", 2: "canary", 3: "authoritative" };

function stageLabel(v) {
  return STAGE_CARDS.find((s) => s.value === v)?.label || v;
}

function DDoSStageControl({ siteUid, disabled: disabledProp = false }) {
  const {
    ddosConfig,
    detectorStage,
    getDetectorStage,
    setDetectorStage,
    clearDetectorStage,
  } = useDdosConfig();

  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (siteUid) getDetectorStage(siteUid);
  }, [siteUid, getDetectorStage]);

  // `stage` nullable → Auto. Non-null → explicit pin.
  const pinned = detectorStage?.stage || null;
  const auto = !pinned;

  const derived = AUTO_STAGE_FROM_SENSITIVITY[ddosConfig?.sensitivity] || "shadow";
  // Which card lights up: the pinned stage when manual, the derived
  // stage when Auto.
  const effective = pinned || derived;

  const disabled = disabledProp || saving || !siteUid;

  const toggleAuto = async () => {
    if (disabled) return;
    setSaving(true);
    try {
      if (auto) {
        // Auto → manual: pin to the currently-derived stage so the
        // user has an explicit starting point they can retune.
        await setDetectorStage(siteUid, derived);
      } else {
        await clearDetectorStage(siteUid);
      }
    } finally {
      setSaving(false);
    }
  };

  const pickStage = async (next) => {
    if (disabled || next === pinned) return;
    setSaving(true);
    try {
      // Clicking a card implies "pin to this stage" — overrides Auto
      // and persists the explicit choice.
      await setDetectorStage(siteUid, next);
    } finally {
      setSaving(false);
    }
  };

  const autoHint = auto
    ? `Follows sensitivity → ${stageLabel(derived)}`
    : `Override active → ${stageLabel(pinned)}`;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <GreenSwitch
          size="small"
          checked={auto}
          onChange={toggleAuto}
          disabled={disabled}
          inputProps={{ "aria-label": "Follow sensitivity (Auto)" }}
        />
        <Typography sx={{ fontSize: 13, color: FG1, fontWeight: 500 }}>Auto</Typography>
        <Typography sx={{ fontSize: 12.5, color: FG2 }}>{autoHint}</Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
          gap: 1,
        }}
      >
        {STAGE_CARDS.map((s) => {
          const on = s.value === effective;
          return (
            <Box
              key={s.value}
              onClick={() => pickStage(s.value)}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-pressed={on}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  pickStage(s.value);
                }
              }}
              sx={{
                p: 1.5,
                borderRadius: "8px",
                border: `1px solid ${on ? INDIGO : BORDER}`,
                background: on ? INDIGO_BG : "#fff",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.55 : 1,
                transition: "border-color 150ms, background 150ms",
                outline: "none",
                minWidth: 0,
                "&:hover": disabled ? {} : { borderColor: on ? INDIGO : BORDER_STRONG },
                "&:focus-visible": { boxShadow: `0 0 0 2px ${INDIGO_RING}` },
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.75}>
                <Typography
                  sx={{
                    fontSize: 13,
                    lineHeight: "20px",
                    fontWeight: 600,
                    color: on ? INDIGO : FG1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: FG3,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                    flexShrink: 0,
                  }}
                >
                  {s.from}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 12, lineHeight: "17px", color: FG2, mt: 0.5 }}>
                {s.desc}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}

export default DDoSStageControl;
